import { formatPenaltyReason } from "./penaltyFormat";
import { attachTransparencyFields } from "./requestTransparency";

export { formatPenaltyReason };

const DEPT_CODE_TO_FRONTEND = {
  HR: "hr-dept",
  OPS: "operations",
  MKT: "marketing",
  ACC: "accounts",
  WH: "warehouse",
  IC: "internal-control",
};

const LEAVE_TYPE_LABELS = {
  ANNUAL: "إجازة اعتيادية",
  SICK: "إجازة مرضية",
  CASUAL: "إجازة عارضة",
  UNPAID: "إجازة على الحساب الخاص",
  COMPENSATORY: "إجازة تعويضية",
  SPECIAL: "إجازة ممنوحة",
};

const HALF_DAY_PERIOD_LABELS = {
  FIRST_HALF: "أول الشيفت",
  SECOND_HALF: "آخر الشيفت",
};

const WEEKDAY_TO_FRONTEND = {
  SUNDAY: "sunday",
  MONDAY: "monday",
  TUESDAY: "tuesday",
  WEDNESDAY: "wednesday",
  THURSDAY: "thursday",
  FRIDAY: "friday",
  SATURDAY: "saturday",
};

const WEEKDAY_LABELS = {
  SUNDAY: "الأحد",
  MONDAY: "الإثنين",
  TUESDAY: "الثلاثاء",
  WEDNESDAY: "الأربعاء",
  THURSDAY: "الخميس",
  FRIDAY: "الجمعة",
  SATURDAY: "السبت",
  sunday: "الأحد",
  monday: "الإثنين",
  tuesday: "الثلاثاء",
  wednesday: "الأربعاء",
  thursday: "الخميس",
  friday: "الجمعة",
  saturday: "السبت",
};

const REQUEST_STATUS_TO_FRONTEND = {
  PENDING: "pending",
  IN_PROGRESS: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
  CLOSED: "closed",
};

const COMPLAINT_STATUS_TO_FRONTEND = {
  OPEN: "Pending",
  IN_PROGRESS: "In Progress",
  ESCALATED: "In Progress",
  RESOLVED: "Resolved",
  REJECTED: "Rejected",
  CLOSED: "Resolved",
};

/** Virtual key for employees who work in administration (no branch assigned) */
export const ADMINISTRATION_KEY = "administration";

/** Branch if the employee works in a field branch, otherwise department. */
export function formatWorkplaceLabel(branchName, departmentName) {
  const branch = String(branchName || "").trim();
  const dept = String(departmentName || "").trim();
  if (branch) {
    return /فرع/.test(branch) ? branch : `فرع ${branch}`;
  }
  if (dept) {
    return /إدارة|ادارة/.test(dept) ? dept : `إدارة ${dept}`;
  }
  return "";
}

export function workplaceFromProfile(source) {
  if (!source) {
    return { branchName: "", departmentName: "", workplaceLabel: "" };
  }
  const nested = source.employee || {};
  const branchName =
    source.branchName ||
    source.branch?.name ||
    nested.branchName ||
    nested.branch?.name ||
    "";
  const departmentName =
    source.departmentName ||
    source.department?.name ||
    nested.departmentName ||
    nested.department?.name ||
    "";
  return {
    branchName,
    departmentName,
    workplaceLabel: formatWorkplaceLabel(branchName, departmentName),
  };
}

function isTeamLeaderJobTitle(jobTitle) {
  if (!jobTitle) return false;
  const compact = String(jobTitle).replace(/[\s\-_\/]+/g, "");
  const lower = compact.toLowerCase();
  return compact.includes("تيمليدر") || lower.includes("teamleader");
}

function inferMemberLevel(emp) {
  if (emp.roleSlugs?.includes("gm")) return "gm";
  if (emp.roleSlugs?.includes("owner")) return "owner";
  if (emp.roleSlugs?.includes("department_manager") || emp.roleSlugs?.includes("hr")) {
    return "department-manager";
  }
  if (emp.roleSlugs?.includes("regional_manager")) return "regional-manager";
  if (emp.roleSlugs?.includes("branch_manager")) return "branch-manager";
  if (emp.jobTitle?.includes("مدير إدارة")) return "department-manager";
  if (emp.jobTitle === "مدير منطقة" || emp.jobTitle?.includes("مدير منطقة")) {
    return "regional-manager";
  }
  if (
    emp.jobTitle?.includes("مدير قسم") ||
    emp.jobTitle?.includes("رئيس قسم")
  ) {
    return "section-manager";
  }
  if (emp.jobTitle === "مدير فرع") return "branch-manager";
  if (emp.jobTitle === "مشرف") return "supervisor";
  if (isTeamLeaderJobTitle(emp.jobTitle)) {
    const hasFieldBranch = Boolean(emp.branchId || emp.branch?.id);
    return hasFieldBranch ? "supervisor" : "section-manager";
  }
  // "manager" role: non-ops = section manager; OPS with branch = supervisor;
  // OPS without branch = HQ section manager (e.g. المخزن)
  if (emp.roleSlugs?.includes("manager")) {
    const deptCode = emp.department?.code;
    const frontendDept =
      DEPT_CODE_TO_FRONTEND[deptCode] || emp.departmentId;
    const isOps = frontendDept === "operations" || deptCode === "OPS";
    if (!isOps) return "section-manager";
    const hasBranch = Boolean(emp.branchId || emp.branch?.id);
    return hasBranch ? "supervisor" : "section-manager";
  }
  return "employee";
}

export function toDateOnlyIso(value) {
  if (!value) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;

  const utcMidnight =
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;

  if (utcMidnight) {
    return d.toISOString().slice(0, 10);
  }

  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function calcDaysBetween(start, end) {
  const s = toDateOnlyIso(start);
  const e = toDateOnlyIso(end);
  if (!s || !e) return 0;
  const [sy, sm, sd] = s.split("-").map(Number);
  const [ey, em, ed] = e.split("-").map(Number);
  const diffDays = Math.round(
    (Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd)) /
      (1000 * 60 * 60 * 24),
  );
  return Math.max(1, diffDays + 1);
}

export function mapDepartments(depts = []) {
  return depts.map((d) => ({
    id: DEPT_CODE_TO_FRONTEND[d.code] || d.id,
    name: d.name,
    code: d.code,
    backendId: d.id,
    isOperational: Boolean(d.isOperational),
    isActive: d.isActive !== false,
  }));
}

export function mapBranches(branches = []) {
  return branches.map((b) => ({
    id: b.id,
    name: b.name,
    code: b.code,
    backendId: b.id,
    isActive: b.isActive,
    timesheetShiftHours: b.timesheetShiftHours ?? null,
  }));
}

export function mapEmployeeRow(emp) {
  const branchId = emp.branchId || emp.branch?.id || ADMINISTRATION_KEY;
  const annualEntitlement = emp.annualLeaveEntitlement ?? 8;
  const annualUsed = emp.annualLeaveUsed ?? 0;
  const casualEntitlement = emp.casualLeaveEntitlement ?? 0;
  const casualUsed = emp.casualLeaveUsed ?? 0;
  const sickEntitlement = emp.sickLeaveEntitlement ?? 30;
  const sickUsed = emp.sickLeaveUsed ?? 0;
  return {
    id: emp.id,
    userId: emp.userId,
    name: emp.fullName,
    code: emp.employeeCode,
    email: emp.email,
    phone: emp.phone,
    nationalId: emp.nationalId,
    gender: emp.gender,
    birthDate: toDateOnlyIso(emp.birthDate),
    address: emp.address,
    maritalStatus: emp.maritalStatus || null,
    militaryStatus: emp.militaryStatus || null,
    emergencyContactName: emp.emergencyContactName || null,
    emergencyContactPhone: emp.emergencyContactPhone || null,
    emergencyContactRelation: emp.emergencyContactRelation || null,
    bankName: emp.bankName || null,
    bankAccountNumber: emp.bankAccountNumber || null,
    iban: emp.iban || null,
    socialInsuranceNumber: emp.socialInsuranceNumber || null,
    educationLevel: emp.educationLevel || null,
    educationInstitution: emp.educationInstitution || null,
    graduationYear: emp.graduationYear ?? null,
    legalFullName: emp.legalFullName || null,
    phoneSecondary: emp.phoneSecondary || null,
    childrenCount: emp.childrenCount ?? null,
    fatherJob: emp.fatherJob || null,
    motherJob: emp.motherJob || null,
    siblingsCount: emp.siblingsCount ?? null,
    courses: emp.courses || null,
    hasChronicDisease:
      emp.hasChronicDisease === undefined ? null : emp.hasChronicDisease,
    chronicDiseaseDetails: emp.chronicDiseaseDetails || null,
    chronicDiseaseMedication: emp.chronicDiseaseMedication || null,
    hasCompanyRelatives:
      emp.hasCompanyRelatives === undefined ? null : emp.hasCompanyRelatives,
    companyRelativeDetails: emp.companyRelativeDetails || null,
    careerGoal: emp.careerGoal || null,
    profileDeclarationAcceptedAt: emp.profileDeclarationAcceptedAt || null,
    profileCompleteness: emp.profileCompleteness || null,
    profileCompletion: emp.profileCompletion || null,
    jobTitle: emp.jobTitle,
    salary:
      emp.salary === undefined || emp.salary === null
        ? null
        : Number(emp.salary),
    payFrequency: emp.payFrequency === "WEEKLY" ? "WEEKLY" : "MONTHLY",
    status:
      emp.status === "ACTIVE"
        ? "active"
        : emp.status === "ON_LEAVE"
          ? "on-leave"
          : emp.status === "TERMINATED"
            ? "terminated"
            : "inactive",
    joinDate: toDateOnlyIso(emp.hireDate || emp.joinDate),
    managerId: emp.managerId,
    directManagerId: emp.directManagerId ?? null,
    approvalMode: emp.approvalMode ?? "LEGACY",
    managerName: emp.manager?.fullName || null,
    roleSlugs: emp.roleSlugs || [],
    departmentId: DEPT_CODE_TO_FRONTEND[emp.department?.code] || emp.departmentId,
    departmentName: emp.department?.name || null,
    departmentBackendId: emp.departmentId,
    branchBackendId: emp.branchId || null,
    branchName: emp.branch?.name || null,
    lineId: emp.lineId || emp.transportLine?.id || null,
    lineName: emp.transportLine?.name || emp.lineName || null,
    lineArea: emp.transportLine?.area || null,
    managedBranchIds: emp.managedBranchIds || [],
    managedBranches: emp.managedBranches || [],
    memberLevel: inferMemberLevel(emp),
    branchId,
    leaveBalance: {
      annualEntitlement,
      annualUsed,
      annualRemaining: annualEntitlement - annualUsed,
      casualEntitlement,
      casualUsed,
      casualRemaining: casualEntitlement - casualUsed,
      sickEntitlement,
      sickUsed,
      sickRemaining: sickEntitlement - sickUsed,
    },
    advance: {
      ordinary: {
        outstandingAmount: emp.advanceOutstanding ?? 0,
        installmentsTotal: emp.advanceInstallmentsTotal ?? 0,
        installmentsPaid: emp.advanceInstallmentsPaid ?? 0,
      },
      exceptional: {
        outstandingAmount: emp.exceptionalAdvanceOutstanding ?? 0,
        installmentsTotal: emp.exceptionalAdvanceInstallmentsTotal ?? 0,
        installmentsPaid: emp.exceptionalAdvanceInstallmentsPaid ?? 0,
      },
      // Totals for dashboards / home cards (ordinary + exceptional)
      outstandingAmount:
        Number(emp.advanceOutstanding ?? 0) +
        Number(emp.exceptionalAdvanceOutstanding ?? 0),
      installmentsTotal:
        (emp.advanceInstallmentsTotal ?? 0) +
        (emp.exceptionalAdvanceInstallmentsTotal ?? 0),
      installmentsPaid:
        (emp.advanceInstallmentsPaid ?? 0) +
        (emp.exceptionalAdvanceInstallmentsPaid ?? 0),
    },
    weeklyRestDay: emp.weeklyRestDay
      ? WEEKDAY_TO_FRONTEND[emp.weeklyRestDay] || emp.weeklyRestDay.toLowerCase()
      : null,
    weeklyRestDayLabel: emp.weeklyRestDay
      ? WEEKDAY_LABELS[emp.weeklyRestDay] || null
      : null,
    workMode: emp.workMode || "OFFICE",
    manualAttendanceOnly: Boolean(emp.manualAttendanceOnly),
    requiresShiftHandover: Boolean(emp.requiresShiftHandover),
  };
}

export function groupEmployeesByBranch(rows = []) {
  const byBranch = { [ADMINISTRATION_KEY]: [] };
  rows.forEach((row) => {
    const branchId = row.branchId || ADMINISTRATION_KEY;
    if (!byBranch[branchId]) byBranch[branchId] = [];
    byBranch[branchId].push(row);
  });
  return byBranch;
}

export function enrichEmployeesWithDepartmentManagers(rows = []) {
  const deptManagersByDept = {};
  rows.forEach((emp) => {
    if (
      emp.memberLevel === "department-manager" &&
      emp.status !== "terminated"
    ) {
      const deptKey = emp.departmentBackendId || emp.departmentId;
      deptManagersByDept[deptKey] = emp.id;
    }
  });

  return rows.map((emp) => ({
    ...emp,
    departmentManagerId:
      emp.memberLevel === "department-manager"
        ? null
        : deptManagersByDept[emp.departmentBackendId || emp.departmentId] ??
          null,
  }));
}

export function mapEmployeesToBranches(employees = []) {
  const rows = enrichEmployeesWithDepartmentManagers(
    employees.map(mapEmployeeRow),
  );
  return groupEmployeesByBranch(rows);
}

export function mapLeaveToPendingRequest(leave) {
  const workflowType =
    leave.workflow?.requestType ?? leave.request?.workflow?.requestType;
  const isDirectToGm = workflowType === "leave_direct_gm";
  const baseStatus =
    REQUEST_STATUS_TO_FRONTEND[leave.request?.status] ||
    REQUEST_STATUS_TO_FRONTEND[leave.status] ||
    "pending";
  const leaveUser = leave.employee || leave.user || {};
  const profile = leaveUser.employee || {};
  const workplace = workplaceFromProfile(
    profile.department || profile.branch ? profile : leaveUser,
  );

  return attachTransparencyFields(
    {
    id: leave.id,
    requestId: leave.requestId,
    userId: leave.userId || leaveUser.id,
    employeeId: leaveUser.employeeId || profile.id || null,
    employeeName:
      leaveUser.fullName ||
      profile.fullName ||
      leaveUser.username ||
      leaveUser.email ||
      "",
    employeeCode:
      leaveUser.employeeCode ||
      profile.employeeCode ||
      leaveUser.username ||
      "",
    branchName: workplace.branchName,
    departmentName: workplace.departmentName,
    workplaceLabel: workplace.workplaceLabel,
    type: LEAVE_TYPE_LABELS[leave.leaveType] || leave.leaveType,
    startDate: toDateOnlyIso(leave.startDate) || leave.startDate,
    endDate: toDateOnlyIso(leave.endDate) || leave.endDate,
    days:
      leave.days ||
      leave.request?.payload?.days ||
      calcDaysBetween(leave.startDate, leave.endDate),
    halfDayPeriod:
      leave.halfDayPeriod ||
      leave.request?.payload?.halfDayPeriod ||
      null,
    halfDayPeriodLabel:
      HALF_DAY_PERIOD_LABELS[
        leave.halfDayPeriod || leave.request?.payload?.halfDayPeriod
      ] || null,
    reason: leave.reason,
    status:
      isDirectToGm && baseStatus === "pending" ? "gm_pending" : baseStatus,
    submittedAt: leave.createdAt?.slice?.(0, 10) || leave.createdAt,
    stepEnteredAt:
      leave.currentStepEnteredAt ||
      leave.request?.currentStepEnteredAt ||
      leave.createdAt ||
      null,
    currentStep: leave.currentStep ?? leave.request?.currentStep ?? null,
    currentApproverUserId:
      leave.currentApproverUserId ||
      leave.request?.currentApproverUserId ||
      null,
    workflow: leave.workflow ?? leave.request?.workflow ?? null,
    submittedByRole: isDirectToGm ? leave.submittedByRole : undefined,
    leaveTypeCode: String(leave.leaveType || "").toLowerCase() || null,
    _source: "leave",
    _backendId: leave.id,
    },
    leave,
  );
}

const FRONTEND_ROLE_TO_BACKEND_SLUG = {
  manager: "manager",
  "department-manager": "department_manager",
  hr: "hr",
  "hr-staff": "hr_staff",
  gm: "gm",
};

function resolveUserRoleSlugs(user) {
  if (user?.backendRoles?.length) return user.backendRoles;
  return [FRONTEND_ROLE_TO_BACKEND_SLUG[user?.role]].filter(Boolean);
}

/** HR staff may act when the workflow step requires primary `hr` or `hr_staff`. Advances stay exact-match only. */
function matchesApproverSlug(userSlugs, requiredSlug) {
  if (!requiredSlug) return false;
  if (userSlugs.includes(requiredSlug)) return true;
  if (
    (requiredSlug === "hr" || requiredSlug === "hr_staff") &&
    (userSlugs.includes("hr") || userSlugs.includes("hr_staff"))
  ) {
    return true;
  }
  if (
    isDirectManagerFamilySlug(requiredSlug) &&
    userSlugs.some((slug) => isDirectManagerFamilySlug(slug))
  ) {
    return true;
  }
  return false;
}

function isDirectManagerFamilySlug(slug) {
  return (
    slug === "manager" || slug === "team_leader" || slug === "supervisor"
  );
}

function isAssignedDirectManager(entity, user) {
  if (!entity || !user) return false;
  if (
    entity.managerUserId &&
    user.id &&
    String(entity.managerUserId) === String(user.id)
  ) {
    return true;
  }
  if (
    entity.managerId &&
    user.employeeId &&
    String(entity.managerId) === String(user.employeeId)
  ) {
    return true;
  }
  return false;
}

function designatedApproverId(entity) {
  return (
    entity?.currentApproverUserId ||
    entity?.request?.currentApproverUserId ||
    null
  );
}

/** Expanded-chain holder when the API sent it, otherwise the stored workflow step. */
function effectiveApproverSlug(entity) {
  const holder = entity?.transparency?.currentHolder?.roleSlug;
  if (holder) return holder;
  return getActiveWorkflowStep(entity)?.approverRole?.slug || null;
}

function matchesDesignatedApprover(chainApproverId, user) {
  if (!chainApproverId) return true;
  return Boolean(user?.id) && String(chainApproverId) === String(user.id);
}

function matchesWorkflowApprover(entity, user) {
  const requiredSlug = effectiveApproverSlug(entity);
  const chainApproverId = designatedApproverId(entity);

  // GM inbox is only the step that is actually waiting on the GM.
  // Extra roles on the account, or being someone's direct manager, do not count.
  if (user?.role === "gm") {
    if (requiredSlug) {
      if (requiredSlug !== "gm") return false;
      return matchesDesignatedApprover(chainApproverId, user);
    }
    if (entity?.status !== "gm_pending") return false;
    return matchesDesignatedApprover(chainApproverId, user);
  }

  if (chainApproverId) {
    return Boolean(user?.id) && String(chainApproverId) === String(user.id);
  }

  if (!requiredSlug) return false;
  if (matchesApproverSlug(resolveUserRoleSlugs(user), requiredSlug)) {
    return true;
  }
  return (
    isDirectManagerFamilySlug(requiredSlug) &&
    isAssignedDirectManager(entity, user)
  );
}

function getActiveWorkflowStep(entity) {
  const currentStep = entity?.currentStep ?? entity?.request?.currentStep;
  const steps = entity?.workflow?.steps || entity?.request?.workflow?.steps || [];
  return (
    steps.find((step) => Number(step.stepOrder) === Number(currentStep)) ||
    null
  );
}

export function canUserApproveLeaveRequest(leave, user) {
  if (!leave || leave._source !== "leave") return false;
  if (!["pending", "gm_pending"].includes(leave.status)) return false;

  return matchesWorkflowApprover(leave, user);
}

export function canUserApproveAdvanceRequest(advance, user) {
  if (!advance) return false;

  const rawStatus = advance.status ?? advance.request?.status;
  const normalized =
    REQUEST_STATUS_TO_FRONTEND[rawStatus] ||
    (typeof rawStatus === "string" ? rawStatus.toLowerCase() : "");
  if (!["pending", "gm_pending"].includes(normalized)) return false;

  const requiredSlug = effectiveApproverSlug(advance);
  const userSlugs = resolveUserRoleSlugs(user);
  const isOwnAdvance =
    (user?.id &&
      advance.userId &&
      String(advance.userId) === String(user.id)) ||
    (user?.employeeId &&
      advance.employeeId &&
      String(advance.employeeId) === String(user.employeeId));
  if (isOwnAdvance) return false;

  // GM acts on an advance only after HR sends it, while it is still at HR.
  if (user?.role === "gm") {
    const atHr = requiredSlug === "hr" || requiredSlug === "hr_staff";
    return atHr && Boolean(advance.referredToGmAt);
  }

  const chainApproverId = designatedApproverId(advance);
  if (chainApproverId) {
    return Boolean(user?.id) && String(chainApproverId) === String(user.id);
  }

  if (!requiredSlug) return false;

  if (userSlugs.includes(requiredSlug)) return true;

  const managerUserId =
    advance.managerUserId ||
    advance.employee?.employee?.manager?.userId ||
    null;
  const isDirectManagerStep =
    requiredSlug === "manager" ||
    requiredSlug === "team_leader" ||
    requiredSlug === "supervisor";
  if (
    isDirectManagerStep &&
    user?.id &&
    managerUserId &&
    String(managerUserId) === String(user.id)
  ) {
    return true;
  }

  return false;
}

export function canUserApproveOvertimeRequest(overtime, user) {
  if (!overtime || overtime._source !== "overtime") return false;

  const rawStatus = overtime.status ?? overtime.request?.status;
  const normalized =
    REQUEST_STATUS_TO_FRONTEND[rawStatus] ||
    (typeof rawStatus === "string" ? rawStatus.toLowerCase() : "");
  if (!["pending", "gm_pending", "in_progress"].includes(normalized)) {
    return false;
  }

  if (overtime.source === "INVENTORY") {
    const slugs = resolveUserRoleSlugs(user);
    if (
      !slugs.includes("hr") &&
      !slugs.includes("gm") &&
      !slugs.includes("admin")
    ) {
      return false;
    }
  }

  return matchesWorkflowApprover(overtime, user);
}

export function canUserApproveInventoryNight(night, user) {
  if (!night || night._source !== "inventory_night") return false;

  const rawStatus = night.status ?? night.requestStatus;
  const normalized =
    REQUEST_STATUS_TO_FRONTEND[rawStatus] ||
    (typeof rawStatus === "string" ? rawStatus.toLowerCase() : "");
  if (!["pending", "in_progress"].includes(normalized)) return false;

  const activeStep = getActiveWorkflowStep(night);
  const requiredSlug = activeStep?.approverRole?.slug;
  if (!requiredSlug) return false;

  return matchesApproverSlug(resolveUserRoleSlugs(user), requiredSlug);
}

export function mapInventoryNightToPendingRequest(night) {
  const baseStatus =
    REQUEST_STATUS_TO_FRONTEND[night.status] ||
    REQUEST_STATUS_TO_FRONTEND[night.requestStatus] ||
    "pending";

  return {
    id: night.id,
    requestId: night.requestId,
    type: "جرد",
    employeeName: night.branch?.name || "فرع",
    employeeId: null,
    employeeCode: night.branch?.code || null,
    branchName: night.branch?.name || "",
    workplaceLabel: formatWorkplaceLabel(night.branch?.name, ""),
    reason: night.reason || `لجنة جرد ${night.members?.length || 0} أعضاء`,
    from: night.scheduledDate,
    to: night.scheduledDate,
    plannedStart: night.plannedStart,
    plannedEnd: null,
    isNightPolicy: night.isNightPolicy,
    members: night.members || [],
    status: baseStatus,
    submittedAt: night.createdAt?.slice?.(0, 10) || night.createdAt,
    stepEnteredAt:
      night.currentStepEnteredAt ||
      night.request?.currentStepEnteredAt ||
      night.createdAt ||
      null,
    currentStep: night.currentStep ?? null,
    workflow: night.workflow ?? null,
    createdByName: night.createdByName || null,
    _source: "inventory_night",
    _backendId: night.id,
  };
}

export function mapOvertimeRow(overtime) {
  const profile = overtime.employee || {};
  const user = overtime.user || {};
  const workplace = workplaceFromProfile(profile);
  const baseStatus =
    REQUEST_STATUS_TO_FRONTEND[overtime.status] ||
    REQUEST_STATUS_TO_FRONTEND[overtime.request?.status] ||
    "pending";

  return attachTransparencyFields(
    {
    id: overtime.id,
    requestId: overtime.requestId || overtime.request?.id,
    userId: overtime.userId || user.id,
    employeeId: profile.id || overtime.employeeId || null,
    employeeName:
      profile.fullName ||
      overtime.employeeName ||
      user.fullName ||
      user.username ||
      "",
    employeeCode:
      profile.employeeCode ||
      overtime.employeeCode ||
      user.employeeCode ||
      user.username ||
      "",
    departmentName: workplace.departmentName,
    branchName: workplace.branchName,
    workplaceLabel: workplace.workplaceLabel,
    hours: Number(overtime.hours ?? 0),
    startDate:
      overtime.startDate?.slice?.(0, 10) ||
      String(overtime.startDate || "").slice(0, 10),
    endDate:
      overtime.endDate?.slice?.(0, 10) ||
      String(overtime.endDate || "").slice(0, 10),
    reason: overtime.reason || "",
    type: "إضافي",
    source: overtime.source || null,
    requestedSettlement: overtime.requestedSettlement || null,
    settlement: overtime.settlement || null,
    attendanceRecordIds: overtime.attendanceRecordIds || [],
    approvedAt: overtime.approvedAt || null,
    status: baseStatus,
    submittedAt: overtime.createdAt?.slice?.(0, 10) || overtime.createdAt,
    stepEnteredAt:
      overtime.currentStepEnteredAt ||
      overtime.request?.currentStepEnteredAt ||
      overtime.createdAt ||
      null,
    currentStep: overtime.currentStep ?? overtime.request?.currentStep ?? null,
    currentApproverUserId:
      overtime.currentApproverUserId ||
      overtime.request?.currentApproverUserId ||
      null,
    workflow: overtime.workflow ?? overtime.request?.workflow ?? null,
    createdByName: overtime.createdByName || null,
    _source: "overtime",
    _backendId: overtime.id,
    },
    overtime,
  );
}

export function mapWeeklyRestToPendingRequest(weeklyRest) {
  const status =
    REQUEST_STATUS_TO_FRONTEND[weeklyRest.status] ||
    REQUEST_STATUS_TO_FRONTEND[weeklyRest.request?.status] ||
    "pending";
  const user = weeklyRest.employee || weeklyRest.user || {};
  const profile = user.employee || {};
  const workplace = workplaceFromProfile(
    profile.department || profile.branch ? profile : user,
  );
  const restDay =
    WEEKDAY_TO_FRONTEND[weeklyRest.restDay] ||
    weeklyRest.restDay?.toLowerCase?.() ||
    weeklyRest.restDay;

  return {
    id: weeklyRest.id,
    requestId: weeklyRest.requestId || weeklyRest.request?.id,
    userId: weeklyRest.userId || user.id,
    employeeId: profile.id || null,
    employeeName:
      profile.fullName || user.fullName || user.username || user.email || "",
    employeeCode:
      profile.employeeCode || user.employeeCode || user.username || "",
    branchName: workplace.branchName,
    departmentName: workplace.departmentName,
    workplaceLabel: workplace.workplaceLabel,
    type: "راحة أسبوعية",
    category: "weekly-rest",
    weeklyRestDay: restDay,
    weeklyRestDayLabel:
      weeklyRest.restDayLabel || WEEKDAY_LABELS[weeklyRest.restDay] || restDay,
    previousDay: weeklyRest.previousDay
      ? WEEKDAY_TO_FRONTEND[weeklyRest.previousDay] ||
        weeklyRest.previousDay.toLowerCase()
      : null,
    reason: weeklyRest.reason,
    status,
    submittedAt: weeklyRest.createdAt?.slice?.(0, 10) || weeklyRest.createdAt,
    stepEnteredAt:
      weeklyRest.currentStepEnteredAt ||
      weeklyRest.request?.currentStepEnteredAt ||
      weeklyRest.createdAt ||
      null,
    currentStep: weeklyRest.currentStep ?? weeklyRest.request?.currentStep ?? null,
    workflow: weeklyRest.workflow ?? weeklyRest.request?.workflow ?? null,
    _source: "weekly-rest",
    _backendId: weeklyRest.id,
  };
}

export function canUserApproveWeeklyRestRequest(weeklyRest, user) {
  if (!weeklyRest || weeklyRest._source !== "weekly-rest") return false;
  if (weeklyRest.status !== "pending") return false;

  return matchesWorkflowApprover(weeklyRest, user);
}

const TIMESHEET_STATUS_TO_FRONTEND = {
  DRAFT: "draft",
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
};

export function mapTimesheetToPendingRequest(timesheet) {
  const status =
    TIMESHEET_STATUS_TO_FRONTEND[timesheet.status] ||
    REQUEST_STATUS_TO_FRONTEND[timesheet.request?.status] ||
    "pending";

  return {
    id: timesheet.id,
    requestId: timesheet.requestId || timesheet.request?.id,
    userId: timesheet.createdById || timesheet.createdBy?.id,
    employeeName:
      timesheet.createdBy?.username ||
      timesheet.branch?.name ||
      timesheet.department?.name ||
      "مدير فرع",
    employeeCode: timesheet.branch?.code || timesheet.department?.code || "",
    type:
      timesheet.scope === "WAREHOUSE" ||
      timesheet.type === "خطة المخزن الأسبوعية" ||
      timesheet.type === "خطة المخزن اليومية"
        ? "خطة المخزن الأسبوعية"
        : timesheet.scope === "MODERATOR" ||
            timesheet.type === "خطة الموديريتور الأسبوعية"
          ? "خطة الموديريتور الأسبوعية"
          : "خطة أسبوعية",
    category: "timesheet",
    branchId: timesheet.branchId,
    branchName: timesheet.branch?.name || timesheet.department?.name,
    departmentName: timesheet.department?.name || "",
    workplaceLabel: formatWorkplaceLabel(
      timesheet.branch?.name,
      timesheet.department?.name,
    ),
    departmentId: timesheet.departmentId,
    weekStartDate: timesheet.weekStartDate,
    weekEndDate: timesheet.weekEndDate,
    entryCount: timesheet.entryCount,
    employees: timesheet.employees || [],
    status,
    submittedAt: timesheet.createdAt?.slice?.(0, 10) || timesheet.createdAt,
    stepEnteredAt:
      timesheet.currentStepEnteredAt ||
      timesheet.request?.currentStepEnteredAt ||
      timesheet.createdAt ||
      null,
    currentStep: timesheet.currentStep ?? timesheet.request?.currentStep ?? null,
    workflow: timesheet.workflow ?? timesheet.request?.workflow ?? null,
    isAmendment: Boolean(
      timesheet.request?.payload?.isAmendment || timesheet.requiresResubmit,
    ),
    requestTitle: timesheet.request?.title || null,
    scope: timesheet.scope || null,
    createdByDepartmentId: timesheet.createdByDepartmentId || null,
    createdBy: timesheet.createdBy || null,
    _source: "timesheet",
    _backendId: timesheet.id,
  };
}

/** Rest days from fully approved weekly/warehouse timesheets, for the approvals calendar. */
export function collectApprovedTimesheetRestDays(timesheets = []) {
  const entries = [];
  for (const ts of timesheets) {
    const status = String(ts.status || "").toLowerCase();
    if (status !== "approved") continue;
    for (const emp of ts.employees || []) {
      const name = emp.employeeName || emp.fullName || "موظف";
      for (const entry of emp.entries || []) {
        if (entry.dayType !== "REST") continue;
        const date = String(entry.date || "").slice(0, 10);
        if (!date) continue;
        entries.push({
          date,
          employee: name,
          type: "راحة أسبوعية",
        });
      }
    }
  }
  return entries;
}

export function canUserApproveTimesheetRequest(timesheet, user) {
  if (!timesheet || timesheet._source !== "timesheet") return false;
  if (timesheet.status !== "pending") return false;
  if (timesheet.scope === "BRANCH" || timesheet.scope === "CASHIER" || timesheet.scope === "WAREHOUSE" || timesheet.scope === "MODERATOR") {
    return false;
  }

  // Branch/cashier weekly plans: creator is final. HR observes other timesheets for visibility only.
  const slugs = resolveUserRoleSlugs(user);
  if (
    (slugs.includes("hr") || slugs.includes("hr_staff")) &&
    !slugs.some((s) =>
      ["regional_manager", "department_manager", "gm", "admin"].includes(s),
    )
  ) {
    return false;
  }

  const currentStep = Number(
    timesheet.currentStep ?? timesheet.request?.currentStep,
  );
  const steps = timesheet.workflow?.steps || [];
  const activeStep = steps.find(
    (step) => Number(step.stepOrder) === currentStep,
  );
  const requiredSlug = activeStep?.approverRole?.slug;
  if (!requiredSlug) return false;
  if (!matchesApproverSlug(slugs, requiredSlug)) return false;

  if (requiredSlug === "manager" || requiredSlug === "team_leader") {
    const requesterManagerUserId =
      timesheet.createdBy?.employee?.manager?.userId || null;
    if (requesterManagerUserId) {
      return requesterManagerUserId === user.id;
    }
    const requesterManagerEmployeeId =
      timesheet.createdBy?.employee?.managerId || null;
    if (requesterManagerEmployeeId) {
      return (
        requesterManagerEmployeeId === user.employeeId ||
        requesterManagerEmployeeId === user.employeeBackendId
      );
    }
    const userDeptId = user.departmentBackendId || null;
    if (userDeptId && timesheet.departmentId) {
      return timesheet.departmentId === userDeptId;
    }
    return false;
  }

  // Branch timesheet → only the RM assigned to that branch.
  if (requiredSlug === "regional_manager") {
    const managed = user.managedBranchIds || [];
    return Boolean(
      timesheet.branchId && managed.includes(timesheet.branchId),
    );
  }

  // OPS / ACC / MKT DM — only their department.
  if (requiredSlug === "department_manager") {
    const userDeptId = user.departmentBackendId || null;
    if (!userDeptId) return false;
    if (timesheet.departmentId) {
      return timesheet.departmentId === userDeptId;
    }
    // Branch/cashier sheets: creator department must match (OPS for BM, ACC for cashier).
    const creatorDeptId =
      timesheet.createdByDepartmentId ||
      timesheet.createdBy?.employee?.departmentId ||
      null;
    if (creatorDeptId) {
      return creatorDeptId === userDeptId;
    }
    // Fallback when creator has no department on record: route by timesheet scope.
    const deptKey = String(user.departmentId || "").toLowerCase();
    if (timesheet.scope === "CASHIER") {
      return deptKey === "accounts" || deptKey === "acc";
    }
    if (timesheet.scope === "DEPARTMENT") {
      return deptKey === "marketing" || deptKey === "mkt";
    }
    if (timesheet.scope === "MODERATOR") {
      return true;
    }
    if (timesheet.scope === "WAREHOUSE") {
      return deptKey === "warehouse" || deptKey === "wh";
    }
    // BRANCH (ops) → operations DM
    return deptKey === "operations" || deptKey === "ops";
  }

  return true;
}

export function mapPermitToPendingRequest(permit) {
  const workflowType =
    permit.workflow?.requestType ?? permit.request?.workflow?.requestType;
  const isDirectToGm = workflowType === "permit_direct_gm";
  const baseStatus =
    REQUEST_STATUS_TO_FRONTEND[permit.status] ||
    REQUEST_STATUS_TO_FRONTEND[permit.request?.status] ||
    "pending";
  const user = permit.employee || permit.user || {};
  const profile = user.employee || {};
  const workplace = workplaceFromProfile(
    profile.department || profile.branch ? profile : user,
  );

  return attachTransparencyFields(
    {
    id: permit.id,
    requestId: permit.requestId || permit.request?.id,
    userId: permit.userId || user.id,
    employeeId: profile.id || null,
    employeeName:
      profile.fullName || user.fullName || user.username || user.email || "",
    employeeCode:
      profile.employeeCode || user.employeeCode || user.username || "",
    branchName: workplace.branchName,
    departmentName: workplace.departmentName,
    workplaceLabel: workplace.workplaceLabel,
    type: permit.isCompensatory ? "إذن تعويضي" : "إذن",
    category: "permit",
    startDate: permit.permitDate,
    permitStartTime: permit.startTime,
    permitEndTime: permit.endTime,
    hours: Number(permit.hours ?? 0),
    deductedHours: Number(permit.deductedHours ?? 0),
    isCompensatory: Boolean(permit.isCompensatory),
    reason: permit.reason,
    status:
      isDirectToGm && baseStatus === "pending" ? "gm_pending" : baseStatus,
    submittedAt: permit.createdAt?.slice?.(0, 10) || permit.createdAt,
    stepEnteredAt:
      permit.currentStepEnteredAt ||
      permit.request?.currentStepEnteredAt ||
      permit.createdAt ||
      null,
    currentStep: permit.currentStep ?? permit.request?.currentStep ?? null,
    currentApproverUserId:
      permit.currentApproverUserId ||
      permit.request?.currentApproverUserId ||
      null,
    workflow: permit.workflow ?? permit.request?.workflow ?? null,
    _source: "permit",
    _backendId: permit.id,
    },
    permit,
  );
}

export function canUserApprovePermitRequest(permit, user) {
  if (!permit || permit._source !== "permit") return false;
  if (!["pending", "gm_pending"].includes(permit.status)) return false;

  return matchesWorkflowApprover(permit, user);
}

/** Split embedded guarantor from advance reason text (`... | ضامن: Name`). */
export function parseAdvanceReasonDetails(reason) {
  const raw = String(reason || "").trim();
  if (!raw) return { reasonText: "", guarantorName: "" };

  const guarantorMatch = raw.match(/(?:^|\|)\s*ضامن:\s*(.+)$/u);
  if (!guarantorMatch) {
    return { reasonText: raw, guarantorName: "" };
  }

  const guarantorName = guarantorMatch[1].trim();
  const reasonText = raw
    .replace(/\s*\|\s*ضامن:\s*.+$/u, "")
    .replace(/^ضامن:\s*.+$/u, "")
    .trim();

  return { reasonText, guarantorName };
}

export function mapAdvanceRow(advance) {
  const profile = advance.employee?.employee || advance.employee || {};
  const user = advance.employee || advance.user || {};
  const workflowType =
    advance.workflow?.requestType ?? advance.request?.workflow?.requestType;
  const isDirectToGm = workflowType === "advance_direct_gm";
  const baseStatus =
    REQUEST_STATUS_TO_FRONTEND[advance.status] ||
    REQUEST_STATUS_TO_FRONTEND[advance.request?.status] ||
    "pending";
  const installments = Number(advance.installments ?? 1) || 1;
  const isExceptional = installments > 1;
  const { reasonText, guarantorName } = parseAdvanceReasonDetails(
    advance.reason,
  );
  const workplace = workplaceFromProfile(profile);

  return attachTransparencyFields(
    {
    id: advance.id,
    requestId: advance.requestId || advance.request?.id,
    userId: advance.userId || user.id,
    managerUserId: advance.managerUserId || profile.manager?.userId || null,
    employeeId: profile.id || null,
    employeeName:
      profile.fullName || user.fullName || user.username || user.email || "",
    employeeCode: profile.employeeCode || user.employeeCode || user.username || "",
    departmentName: workplace.departmentName,
    branchName: workplace.branchName,
    workplaceLabel: workplace.workplaceLabel,
    amount: Number(advance.amount ?? 0),
    installments,
    isExceptional,
    advanceKindLabel: isExceptional ? "استثنائية" : "عادية",
    guarantorName,
    reasonText,
    reason: advance.reason || "",
    type: "سلفة",
    approvedAt: advance.approvedAt || null,
    status:
      isDirectToGm && baseStatus === "pending" ? "gm_pending" : baseStatus,
    submittedAt: advance.createdAt?.slice?.(0, 10) || advance.createdAt,
    stepEnteredAt:
      advance.currentStepEnteredAt ||
      advance.request?.currentStepEnteredAt ||
      advance.createdAt ||
      null,
    currentStep: advance.currentStep ?? advance.request?.currentStep ?? null,
    currentApproverUserId:
      advance.currentApproverUserId ||
      advance.request?.currentApproverUserId ||
      null,
    workflow: advance.workflow ?? advance.request?.workflow ?? null,
    disbursementStatus: advance.disbursementStatus || null,
    disbursementTargetType: advance.disbursementTargetType || null,
    disbursementAssignedAt: advance.disbursementAssignedAt || null,
    disbursementConfirmedAt: advance.disbursementConfirmedAt || null,
    disbursementPostponedUntil: advance.disbursementPostponedUntil || null,
    disbursementPostponeReason: advance.disbursementPostponeReason || null,
    referredToGmAt: advance.referredToGmAt || null,
    disbursementAssignee: advance.disbursementAssignee || null,
    disbursementAssigneeLabel: advance.disbursementAssigneeLabel || null,
    assignToAllBranchCashiers: advance.assignToAllBranchCashiers || false,
    disbursementConfirmedBy: advance.disbursementConfirmedBy || null,
    installmentsPaid: Number(advance.installmentsPaid ?? 0),
    remainingAmount: Number(advance.remainingAmount ?? 0),
    disbursementTargets: Array.isArray(advance.disbursementTargets)
      ? advance.disbursementTargets
      : [],
    submittedByRole: isDirectToGm
      ? advance.submittedByRole || "department-manager"
      : undefined,
    _source: "advance",
    _backendId: advance.id,
    },
    advance,
  );
}

export function mapRequestToPendingRequest(req) {
  const payload = req.payload || {};
  const workflowType = req.workflow?.requestType;
  const isDirectToGm = workflowType === "advance_direct_gm";
  const baseStatus = REQUEST_STATUS_TO_FRONTEND[req.status] || "pending";

  return attachTransparencyFields(
    {
    id: req.id,
    requestId: req.id,
    userId: req.createdById,
    employeeName: req.createdBy?.username || req.createdBy?.email || "",
    employeeCode: req.createdBy?.username || "",
    branchName: workplaceFromProfile(req.createdBy).branchName,
    departmentName: workplaceFromProfile(req.createdBy).departmentName,
    workplaceLabel: workplaceFromProfile(req.createdBy).workplaceLabel,
    type: req.title || req.type,
    amount: payload.amount,
    installments: payload.installments,
    hours: payload.hours,
    date: payload.date,
    startDate: payload.startDate,
    days: payload.days,
    reason: payload.reason || req.title,
    status:
      isDirectToGm && baseStatus === "pending" ? "gm_pending" : baseStatus,
    submittedAt: req.createdAt?.slice?.(0, 10) || req.createdAt,
    currentStep: req.currentStep ?? null,
    workflow: req.workflow ?? null,
    submittedByRole: isDirectToGm ? req.submittedByRole : undefined,
    _source: "request",
    _backendId: req.id,
    },
    req,
  );
}

export function mapComplaintRow(complaint) {
  const sender =
    complaint.employee || complaint.user || {};
  const currentApproverRole =
    complaint.currentApproverRole ||
    complaint.request?.workflow?.steps?.find(
      (s) => s.stepOrder === complaint.request?.currentStep,
    )?.approverRole?.slug ||
    null;
  const targetRole =
    complaint.targetRole ||
    complaint.request?.payload?.targetRole ||
    null;
  const receiverRole =
    currentApproverRole === "department_manager"
      ? "department-manager"
      : currentApproverRole === "regional_manager"
        ? "regional-manager"
        : currentApproverRole || "";

  const workplace = workplaceFromProfile(sender);
  const nested = sender.employee || {};
  const jobTitle = sender.jobTitle || nested.jobTitle || null;

  return {
    id: complaint.id,
    title: complaint.title,
    description: complaint.description,
    senderId: sender.id || null,
    senderName:
      sender.fullName ||
      sender.username ||
      sender.email ||
      "",
    jobTitle,
    branchName: workplace.branchName,
    departmentName: workplace.departmentName,
    workplaceLabel: workplace.workplaceLabel,
    senderRole: "employee",
    receiverId: null,
    receiverName: "",
    receiverRole,
    currentApproverRole,
    targetRole,
    assignedUserId: complaint.assignedUserId || null,
    status: COMPLAINT_STATUS_TO_FRONTEND[complaint.status] || complaint.status,
    kind: complaint.kind || complaint.request?.payload?.kind || "COMPLAINT",
    priority: complaint.priority,
    createdAt: complaint.createdAt,
    updatedAt: complaint.updatedAt,
    response: complaint.response || null,
    responseDate: complaint.responseDate || null,
    requestId: complaint.requestId,
    currentStep: complaint.request?.currentStep ?? null,
    actions: Array.isArray(complaint.actions)
      ? complaint.actions.map((a) => ({
          id: a.id,
          action: a.action,
          notes: a.notes || null,
          performedByName: a.performedByName || "",
          createdAt: a.createdAt,
          newStatus: a.newStatus || null,
        }))
      : [],
    _backendId: complaint.id,
  };
}

export function formatPenaltyDeduction(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${n.toLocaleString("ar-EG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} جنيه`;
}

export function mapPenaltyRow(penalty) {
  const effectiveDate = penalty.effectiveDate || penalty.date;
  const date =
    typeof effectiveDate === "string"
      ? effectiveDate.slice(0, 10)
      : effectiveDate
        ? new Date(effectiveDate).toISOString().slice(0, 10)
        : "";

  const status = penalty.status || "active";
  const amount = penalty.amount ?? null;
  const workplace = workplaceFromProfile(penalty);

  return {
    id: penalty.id,
    employeeId: penalty.employeeId,
    employeeName: penalty.employeeName || null,
    employeeCode: penalty.employeeCode || null,
    jobTitle: penalty.jobTitle || null,
    branchName: workplace.branchName || null,
    departmentName: workplace.departmentName || null,
    workplaceLabel: workplace.workplaceLabel || null,
    type: penalty.typeLabel || penalty.type,
    backendType: penalty.type,
    date,
    days: penalty.days != null && penalty.days !== "" ? Number(penalty.days) : null,
    amount,
    deductionLabel: formatPenaltyDeduction(amount),
    reason: formatPenaltyReason(penalty.reason),
    status,
    appealStatus:
      status === "appealed"
        ? penalty.appealStatus || "معلق"
        : penalty.appealStatus || null,
    createdById: penalty.createdById || null,
    createdByName: penalty.createdByName || null,
    reviewedById: penalty.reviewedById || null,
    reviewedByName: penalty.reviewedByName || null,
    reviewedAt: penalty.reviewedAt || null,
    reviewNote: penalty.reviewNote || null,
    createdAt: penalty.createdAt,
    _backendId: penalty.id,
  };
}

export function resolveFrontendDepartmentId(
  departmentId,
  departments = [],
  departmentCode,
) {
  if (!departmentId && !departmentCode) return "";
  if (departmentCode && DEPT_CODE_TO_FRONTEND[departmentCode]) {
    return DEPT_CODE_TO_FRONTEND[departmentCode];
  }
  const byId = departments.find((d) => d.id === departmentId);
  if (byId) return byId.id;
  const byBackend = departments.find((d) => d.backendId === departmentId);
  if (byBackend) return byBackend.id;
  return departmentId || "";
}

export function resolveBackendDepartmentId(frontendDeptId, departments = []) {
  if (!frontendDeptId) return null;
  const byFrontend = departments.find((d) => d.id === frontendDeptId);
  if (byFrontend?.backendId) return byFrontend.backendId;
  const byBackend = departments.find((d) => d.backendId === frontendDeptId);
  if (byBackend?.backendId) return byBackend.backendId;
  const code = Object.entries(DEPT_CODE_TO_FRONTEND).find(
    ([, v]) => v === frontendDeptId,
  )?.[0];
  if (code) {
    return departments.find((d) => d.code === code)?.backendId ?? null;
  }
  return frontendDeptId;
}

export function resolveBackendBranchId(frontendBranchId, branches = []) {
  if (!frontendBranchId || frontendBranchId === ADMINISTRATION_KEY) {
    return null;
  }
  const match = branches.find(
    (b) => b.id === frontendBranchId || b.backendId === frontendBranchId,
  );
  return match?.backendId || match?.id || frontendBranchId;
}

export function getBranchDisplayOptions(branches = []) {
  return [
    { id: ADMINISTRATION_KEY, name: "الإدارة" },
    ...branches,
  ];
}

export function suggestBranchCode(name) {
  const latin = name
    .trim()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 6);
  return latin.length >= 2 ? latin : `B${Date.now().toString().slice(-4)}`;
}

export function getBranchLabel(branchId, branchMap = {}) {
  if (!branchId || branchId === ADMINISTRATION_KEY) return "الإدارة";
  return branchMap[branchId] || branchId;
}
